import React from 'react';
import { Box, Chip, Tooltip } from '@mui/material';
import { Language as WebIcon } from '@mui/icons-material';
import WebSourceBadge from './WebSourceBadge';
import type { WebSourceReference } from '../hooks/useChat';

const COLLAPSE_THRESHOLD = 3;

export interface InlineWebSourceBadgesProps {
  webSourceIds: number[];
  webSources: WebSourceReference[];
}

/**
 * Inline counterpart to InlineSourceBadges for [Web:N] markers. Resolves ids
 * against the per-message `webSources` array (no async fetch — unlike DB
 * sources, web results are inlined in the chat response).
 */
const InlineWebSourceBadges: React.FC<InlineWebSourceBadgesProps> = ({ webSourceIds, webSources }) => {
  const resolved = React.useMemo(
    () => webSourceIds
      .map(id => webSources.find(s => s.id === id))
      .filter((s): s is WebSourceReference => Boolean(s)),
    [webSourceIds, webSources]
  );

  if (resolved.length === 0) return null;

  const handleMultiClick = () => {
    resolved.forEach(s => window.open(s.url, '_blank', 'noopener,noreferrer'));
  };

  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        gap: 0.5,
        alignItems: 'center',
        verticalAlign: 'middle',
        my: 0.25,
      }}
    >
      {resolved.length >= COLLAPSE_THRESHOLD ? (
        <Tooltip
          arrow
          title={resolved.map(s => s.title || s.url).join(' • ')}
        >
          <Chip
            icon={<WebIcon sx={{ fontSize: '0.875rem' }} />}
            label={`Multiple web sources (${resolved.length})`}
            size="small"
            variant="outlined"
            color="info"
            onClick={handleMultiClick}
            sx={{
              cursor: 'pointer',
              fontSize: '0.75rem',
              height: 24,
              '& .MuiChip-label': { px: 0.5 },
              '& .MuiChip-icon': { ml: 0.5 },
              '&:hover': { backgroundColor: 'action.hover' },
            }}
          />
        </Tooltip>
      ) : (
        resolved.map(source => (
          <WebSourceBadge key={source.id} source={source} />
        ))
      )}
    </Box>
  );
};

export default InlineWebSourceBadges;
